import { MNSproCloudApi } from '..';
import { timePatternResponseToTimePattern } from '../../utils/mappings/timePatternMappings';
import {
  Booking,
  BookingOptions,
  Equipment,
  EquipmentOptions,
  Room,
  RoomOptions,
} from '../api/v2';

export class ResourceService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ResourcesApiFactory(config);

    return api;
  }

  static async getActiveTimepattern(authToken: string, organisationId: string) {
    const api = ResourceService.getApi(authToken);
    const response = await api.getTheActiveTimePattern(organisationId);

    return timePatternResponseToTimePattern(response.data);
  }

  static async getBookingPeople(authToken: string, organisationId: string) {
    const api = ResourceService.getApi(authToken);
    const response = await api.getBookingPeople(organisationId);

    return response.data;
  }

  static async getMyBookings(
    authToken: string,
    organisationId: string,
    from: string,
    to: string,
    timePeriod: string,
  ): Promise<Booking[]> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getMyBookings(
      organisationId,
      from,
      timePeriod,
      to,
    );

    return response.data;
  }

  static async getAllEquipment(
    authToken: string,
    organizationId: string,
  ): Promise<Equipment[]> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getAllEquipment(organizationId);

    return response.data;
  }

  static async getEquipmentById(
    authToken: string,
    organisationId: string,
    resourceId: string,
  ): Promise<Equipment> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getEquipment(organisationId, resourceId);

    return response.data;
  }

  static async updateEquipment(
    authToken: string,
    organisationId: string,
    resourceId: string,
    equipmentOptions: EquipmentOptions,
  ): Promise<Equipment> {
    const api = ResourceService.getApi(authToken);
    const response = await api.updateEquipment(
      organisationId,
      resourceId,
      equipmentOptions,
    );

    return response.data;
  }

  static async createEquipment(
    authToken: string,
    organisationId: string,
    equipmentOptions: EquipmentOptions,
  ): Promise<Equipment> {
    const api = ResourceService.getApi(authToken);
    const response = await api.createEquipment(
      organisationId,
      equipmentOptions,
    );

    return response.data;
  }

  static async deleteEquipment(
    authToken: string,
    organisationId: string,
    resourceId: string,
  ): Promise<void> {
    const api = ResourceService.getApi(authToken);
    await api.deleteEquipment(organisationId, resourceId);
  }

  static async getEquipmentBookings(
    authToken: string,
    organisationId: string,
    resourceId: string,
    from?: string,
    to?: string,
  ): Promise<Booking[]> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getEquipmentBookings(
      organisationId,
      resourceId,
      from,
      to,
    );

    return response.data;
  }

  static async createEquipmentBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    from: string,
    to: string,
    onBehalfOfUserId?: string,
  ) {
    const api = ResourceService.getApi(authToken);
    const bookingOptions: BookingOptions = {
      from,
      to,
    };

    const response = await api.createEquipmentBooking(
      organizationId,
      resourceId,
      bookingOptions,
      onBehalfOfUserId,
    );

    return response.data;
  }

  static async updateEquipmentBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    bookingId: string,
    from: string,
    to: string,
  ): Promise<Booking> {
    const api = ResourceService.getApi(authToken);
    const bookingOptions = {
      from,
      to,
    };
    const response = await api.updateEquipmentBooking(
      bookingId,
      organizationId,
      resourceId,
      bookingOptions,
    );

    return response.data;
  }

  static async deleteEquipmentBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    bookingId: string,
  ) {
    const api = ResourceService.getApi(authToken);
    await api.deleteEquipmentBooking(bookingId, organizationId, resourceId);
  }

  static async getAllRooms(
    authToken: string,
    organizationId: string,
  ): Promise<Room[]> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getAllRooms(organizationId);

    return response.data;
  }

  static async getRoomById(
    authToken: string,
    organisationId: string,
    resourceId: string,
  ): Promise<Room> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getRoom(organisationId, resourceId);

    return response.data;
  }

  static async getRoomBookings(
    authToken: string,
    organisationId: string,
    resourceId: string,
    from?: string,
    to?: string,
  ): Promise<Booking[]> {
    const api = ResourceService.getApi(authToken);
    const response = await api.getRoomBookings(
      organisationId,
      resourceId,
      from,
      to,
    );

    return response.data;
  }

  static async createRoom(
    authToken: string,
    organisationId: string,
    roomOptions: RoomOptions,
  ): Promise<Room> {
    const api = ResourceService.getApi(authToken);
    const response = await api.createRoom(organisationId, roomOptions);

    return response.data;
  }

  static async updateRoom(
    authToken: string,
    organisationId: string,
    resourceId: string,
    roomOptions: RoomOptions,
  ): Promise<Room> {
    const api = ResourceService.getApi(authToken);
    const response = await api.updateRoom(
      organisationId,
      resourceId,
      roomOptions,
    );
    return response.data;
  }

  static async deleteRoom(
    authToken: string,
    organisationId: string,
    resourceId: string,
  ): Promise<void> {
    const api = ResourceService.getApi(authToken);
    await api.deleteRoom(organisationId, resourceId);
  }

  static async createRoomBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    from: string,
    to: string,
    onBehalfOfUserId?: string,
  ) {
    const api = ResourceService.getApi(authToken);
    const bookingOptions: BookingOptions = {
      from,
      to,
    };
    const response = await api.createRoomBooking(
      organizationId,
      resourceId,
      bookingOptions,
      onBehalfOfUserId,
    );

    return response.data;
  }

  static async updateRoomBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    bookingId: string,
    from: string,
    to: string,
  ): Promise<Booking> {
    const api = ResourceService.getApi(authToken);
    const bookingOptions = {
      from,
      to,
    };
    const response = await api.updateRoomBooking(
      bookingId,
      organizationId,
      resourceId,
      bookingOptions,
    );

    return response.data;
  }

  static async deleteRoomBooking(
    authToken: string,
    organizationId: string,
    resourceId: string,
    bookingId: string,
  ) {
    const api = ResourceService.getApi(authToken);
    await api.deleteRoomBooking(bookingId, organizationId, resourceId);
  }
}
